import React, { useEffect, useState } from 'react'

import { Tag, List, Empty, Spin, Collapse, Tooltip, message } from 'antd'
import { useTranslation } from 'react-i18next'

import { formatDateTime } from '../../../utils/dateTime'

// Source tag colors. Labels resolved via i18n at render time.
const SOURCE_TAG_COLOR = {
  third_party: 'blue',
  partner:     'purple',
  webhook:     'orange'
}

function statusTagColor(status, http) {
  if (status === 'success') return 'success'
  if (status === 'failed')  return 'error'
  if (typeof http === 'number') {
    if (http >= 200 && http < 300) return 'success'
    if (http >= 400 && http < 500) return 'warning'
    if (http >= 500)               return 'error'
  }
  return 'default'
}

const PRE_STYLE = {
  background: '#f5f5f5', padding: '8px', borderRadius: '4px',
  margin: 0, fontSize: '11px', overflow: 'auto', maxHeight: '500px',
  whiteSpace: 'pre-wrap', wordBreak: 'break-word'
}
const LABEL_STYLE = { fontSize: '11px', color: '#999', marginBottom: '4px' }

/**
 * IntegrationLogPanel — 訂單頁「串接紀錄」tab。
 *
 * 來源（3 種，已在後端合併排序）：
 *   - third_party_api_log (我方→partner)：批次呼叫只顯示這張訂單的切片，
 *     後端用 RFC 6901 JSON Pointer 在寫入時記下位置、讀取時 evaluate。
 *   - partner_api_log    (partner→我方)：已 per-order，整筆 payload 顯示。
 *   - webhook_delivery   (我方推送)：已 per-order，整筆 payload 顯示。
 *
 * UI 行為：
 *   1. 載入後一次回傳所有 metadata（list view 不 fetch payload）。
 *   2. 點某 row 才 lazy-fetch slice payload（GET .../integration_logs/:source/:id）。
 *   3. 同一個 row 收合再展開不重 fetch（cache 在 sliceCache）。
 */
export default function IntegrationLogPanel({ orderId, orderService }) {
  const { t } = useTranslation()
  const [logs, setLogs] = useState(null)
  const [loading, setLoading] = useState(false)
  const [sliceCache, setSliceCache] = useState({})

  useEffect(() => {
    if (!orderId) return
    setLoading(true)
    orderService.getIntegrationLogs(orderId)
      .then((res) => setLogs(res?.data?.logs || []))
      .catch(() => {
        message.error(t('common.error_label'))
        setLogs([])
      })
      .finally(() => setLoading(false))
  }, [orderId])

  const sliceKey = (entry) => `${entry.source}:${entry.id}`

  const loadSlice = async (entry) => {
    const key = sliceKey(entry)
    if (sliceCache[key]) return
    try {
      const res = await orderService.getIntegrationLogSlice(orderId, entry.source, entry.id)
      setSliceCache((prev) => ({ ...prev, [key]: res?.data || { found: false } }))
    } catch (e) {
      console.error('[IntegrationLogPanel] slice fetch failed', e)
      setSliceCache((prev) => ({ ...prev, [key]: { found: false } }))
    }
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin /></div>
  }

  if (!logs || logs.length === 0) {
    return <Empty description={t('common.table.no_data')} />
  }

  const renderHeader = (entry) => {
    const sourceColor = SOURCE_TAG_COLOR[entry.source] || 'default'
    const sourceLabel = t(`common.api_logs.source.${entry.source}`, { defaultValue: entry.source })
    // Backend `action` column is the single source of truth — every batch
    // caller passes `action:` to BaseClient#request now.
    const actionLabel = entry.action
      ? t(`common.api_logs.action.${entry.action}`, { defaultValue: entry.action })
      : null
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Tag color={sourceColor}>{sourceLabel}</Tag>
        {actionLabel && <Tag color="blue">{actionLabel}</Tag>}
        {entry.event_type && <Tag>{entry.event_type}</Tag>}
        {entry.is_batch && <Tag color="geekblue">{t('common.api_logs.batch')}</Tag>}
        {entry.request_method && <Tag>{entry.request_method}</Tag>}
        {typeof entry.response_http_status === 'number' && (
          <Tag color={statusTagColor(entry.response_status, entry.response_http_status)}>
            {entry.response_http_status}
          </Tag>
        )}
        {entry.status && <Tag color={statusTagColor(entry.status)}>{entry.status}</Tag>}
        <Tooltip
          title={
            <span style={{ fontSize: '11px', wordBreak: 'break-all' }}>
              {entry.request_path || entry.url}
            </span>
          }
          placement="top"
          mouseEnterDelay={0.2}
          // antd's default popover max-width truncates very long URLs; widen
          // to fit a typical Shopee `get_order_detail?...&order_sn_list=...`
          // which is ~600 chars when access_token is present.
          overlayInnerStyle={{ maxWidth: 720 }}
        >
          <span
            style={{
              fontSize: '12px', color: '#666', flex: 1, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              cursor: 'pointer'
            }}
            onClick={() => {
              navigator.clipboard?.writeText(entry.request_path || entry.url || '')
                .then(() => message.success(t('common.copy_success', { defaultValue: '已複製' })))
                .catch(() => {})
            }}
          >
            {entry.request_path || entry.url}
          </span>
        </Tooltip>
        {typeof entry.duration_ms === 'number' && (
          <span style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap' }}>
            {entry.duration_ms.toFixed(0)} ms
          </span>
        )}
        <span style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap' }}>
          {formatDateTime(entry.occurred_at)}
        </span>
      </div>
    )
  }

  const renderSlice = (entry) => {
    const slice = sliceCache[sliceKey(entry)]
    if (!slice) return <Spin size="small" />
    if (slice.found === false) {
      let msg
      if (slice.expired)           msg = t('common.api_logs.expired')
      else if (slice.slice_failed) msg = t('common.api_logs.slice_failed')
      else                         msg = t('common.error_label')
      return <div style={{ fontSize: '12px', color: '#999' }}>{msg}</div>
    }

    const { request, response, error_message, response_pointers, request_pointers } = slice
    return (
      <div>
        {typeof error_message === 'string' && error_message && (
          <div style={{ marginBottom: '8px', fontSize: '12px', color: '#cf1322' }}>
            <strong>error</strong>: {error_message}
          </div>
        )}
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={LABEL_STYLE}>
              {t('common.api_logs.request')}
              {request_pointers?.length > 0 && (
                <span style={{ marginLeft: 4, color: '#666' }}>({request_pointers.join(', ')})</span>
              )}
            </div>
            <pre style={PRE_STYLE}>
              {request === undefined || request === null ? '-' :
                typeof request === 'string' ? request : JSON.stringify(request, null, 2)}
            </pre>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={LABEL_STYLE}>
              {t('common.api_logs.response')}
              {response_pointers?.length > 0 && (
                <span style={{ marginLeft: 4, color: '#666' }}>({response_pointers.join(', ')})</span>
              )}
            </div>
            <pre style={PRE_STYLE}>
              {response === undefined || response === null ? '-' :
                typeof response === 'string' ? response : JSON.stringify(response, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    )
  }

  return (
    <List
      dataSource={logs}
      pagination={{ pageSize: 10 }}
      size="small"
      renderItem={(entry) => (
        <List.Item style={{ display: 'block', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ marginBottom: '8px' }}>{renderHeader(entry)}</div>
          <Collapse
            size="small"
            onChange={(active) => { if (active.length > 0) loadSlice(entry) }}
            items={[{
              key: 'slice',
              label: <span style={{ fontSize: '12px' }}>{t('common.api_logs.request')} / {t('common.api_logs.response')}</span>,
              children: renderSlice(entry)
            }]}
          />
        </List.Item>
      )}
    />
  )
}
